// v10/src/components/QRModal.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, QrCode } from 'lucide-react';

const APP_URL = process.env.REACT_APP_URL || 'https://condor360.app';

const QRModal = ({ aircraft, darkMode, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!aircraft) return null;

  const qrUrl   = `${APP_URL}/?aircraft=${aircraft.id}`;
  const qrImage = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrUrl)}&bgcolor=ffffff&color=1d4ed8&margin=12`;

  const handleCopy = () => {
    navigator.clipboard.writeText(qrUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrUrl)}&bgcolor=ffffff&color=1d4ed8&margin=20`;
    a.download = `QR_${aircraft.registration || aircraft.model_name}.png`;
    a.target = '_blank';
    a.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.88, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '340px', borderRadius: '28px', overflow: 'hidden', background: darkMode ? 'rgba(10,15,30,0.98)' : 'white', boxShadow: '0 32px 80px rgba(0,0,0,0.4)', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(37,99,235,0.12)' }}
      >
        {/* Header */}
        <div style={{ height: '4px', background: 'linear-gradient(90deg,#2563eb,#7c3aed)' }} />
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <QrCode style={{ width: '18px', height: '18px', color: 'white' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>Código QR</p>
              <p style={{ margin: 0, fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                {aircraft.name}{aircraft.registration ? ` · ${aircraft.registration}` : ''}
              </p>
            </div>
          </div>
          <motion.button onClick={onClose} whileTap={{ scale: 0.9 }}
            style={{ width: '30px', height: '30px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X style={{ width: '14px', height: '14px' }} />
          </motion.button>
        </div>

        {/* QR Image */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{ padding: '12px', borderRadius: '16px', background: 'white', boxShadow: '0 4px 20px rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.1)' }}>
            <img src={qrImage} alt="QR" width={200} height={200} style={{ display: 'block', borderRadius: '8px' }} />
          </div>

          <p style={{ margin: 0, fontSize: '11px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', lineHeight: 1.6 }}>
            Pegá este QR en la cabina del avión. El piloto lo escanea y la app abre directamente en el chequeo de este avión.
          </p>

          {/* URL */}
          <div style={{ width: '100%', padding: '10px 12px', borderRadius: '12px', background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(248,250,255,1)', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <p style={{ margin: 0, fontSize: '10px', fontFamily: 'monospace', color: darkMode ? '#93c5fd' : '#2563eb', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {qrUrl}
            </p>
            <motion.button onClick={handleCopy} whileTap={{ scale: 0.93 }}
              style={{ flexShrink: 0, padding: '4px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: copied ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.1)'), color: copied ? 'white' : (darkMode ? 'rgba(255,255,255,0.6)' : '#2563eb'), fontSize: '10px', fontWeight: 700, transition: 'all 0.2s', fontFamily: 'inherit' }}>
              {copied ? '✓ Copiado' : 'Copiar'}
            </motion.button>
          </div>

          {/* Botón descargar */}
          <motion.button onClick={handleDownload} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
            style={{ width: '100%', padding: '13px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 16px rgba(37,99,235,0.3)', fontFamily: 'inherit' }}>
            <Download style={{ width: '15px', height: '15px' }} />
            Descargar QR para imprimir
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QRModal;
